import React, { useState, useEffect, useCallback } from 'react';
import { portalReportsAPI } from '../../services/portalAPI';
import { reportsAPI, triggerDownload } from '../../services/reportsAPI';
import PortalFooter from '../../components/Layout/PortalFooter';
import { TutorialAutoStart, TutorialHelpButton } from '../../components/Portal/Tutorial';
import '../../styles/portal-brand.css';

interface MonthlyData {
  month: string;
  month_name: string;
  order_count: number;
  total_amount: number;
  total_weight: number;
}

interface ProductData {
  formula_id: number;
  formula_name: string;
  animal_type: string;
  order_count: number;
  total_quantity_lbs: number;
  total_amount: number;
}

interface AnimalData {
  animal_type: string;
  total_quantity_lbs: number;
  total_amount: number;
}

interface YoYComparison {
  prev_year: number;
  prev_total_orders: number;
  prev_total_spent: number;
  prev_total_weight: number;
  order_change_pct: number | null;
  spent_change_pct: number | null;
  weight_change_pct: number | null;
}

interface BatchSummary {
  total_batches: number;
  animals_raised: number;
  animals_finished: number;
  survival_rate: number;
  total_revenue: number;
  total_costs: number;
  net_profit: number;
  by_animal_type: Array<{
    consumption_profile__category: string;
    consumption_profile__display_name: string;
    batch_count: number;
    total_animals: number;
  }>;
}

interface AnnualReportData {
  year: number;
  summary: {
    total_orders: number;
    total_spent: number;
    total_weight_lbs: number;
    avg_order_value: number;
  };
  batch_summary: BatchSummary | null;
  monthly_breakdown: MonthlyData[];
  product_breakdown: ProductData[];
  animal_breakdown: AnimalData[];
  yoy_comparison: YoYComparison;
  available_years: number[];
}

const ANIMAL_COLORS: { [key: string]: string } = {
  'POULTRY': '#E73C3E',
  'SWINE': '#6C8062',
  'CATTLE': '#508590',
  'GOAT': '#C3AD8B',
  'SHEEP': '#3F2021',
  'HORSE': '#8B5CF6',
  'RABBIT': '#EC4899',
  'OTHER': '#94A3B8',
};

const PortalAnnualReport: React.FC = () => {
  const [data, setData] = useState<AnnualReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [downloading, setDownloading] = useState(false);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await portalReportsAPI.getAnnualReport({ year: selectedYear });
      setData(response.data);
    } catch (err: any) {
      setError('Failed to load annual report');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);
      // Use the comprehensive annual report PDF endpoint from the planner
      const blob = await reportsAPI.downloadAnnualReport(selectedYear);
      triggerDownload(blob, `Annual_Report_${selectedYear}.pdf`);
    } catch (err: any) {
      alert('Failed to download PDF. Please try again.');
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatPercent = (value: number | null) => {
    if (value === null) return 'N/A';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  const getChangeColor = (value: number | null) => {
    if (value === null) return '#508590';
    return value >= 0 ? '#059669' : '#DC2626';
  };

  // Calculate max values for bar chart scaling
  const maxMonthlyAmount = data?.monthly_breakdown
    ? Math.max(...data.monthly_breakdown.map(m => m.total_amount))
    : 0;

  const maxProductQuantity = data?.product_breakdown
    ? Math.max(...data.product_breakdown.map(p => p.total_quantity_lbs))
    : 0;

  const totalAnimalQuantity = data?.animal_breakdown
    ? data.animal_breakdown.reduce((sum, a) => sum + a.total_quantity_lbs, 0)
    : 0;

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingState}>Loading annual report...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorState}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <TutorialAutoStart pageKey="annual-report" />
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.title}>Annual Report</h1>
          <p style={styles.subtitle}>Your complete purchasing summary and trends</p>
        </div>
        <TutorialHelpButton pageKey="annual-report" />
      </div>
      <div style={styles.headerActions}>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          style={styles.yearSelect}
          data-tutorial="year-selector"
        >
          {data?.available_years.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
        <button
          onClick={handleDownloadPDF}
          disabled={downloading}
          style={styles.downloadButton}
          data-tutorial="download-pdf"
        >
          {downloading ? 'Generating...' : 'Download PDF'}
        </button>
      </div>

      {data && (
        <>
          {/* Batch Summary Cards (if customer has batches) */}
          {data.batch_summary && (
            <>
              <h2 style={styles.sectionTitle}>Production Summary</h2>
              <div style={styles.summaryGrid} data-tutorial="batch-summary-cards">
                <div style={styles.summaryCard}>
                  <div style={styles.summaryLabel}>Total Batches</div>
                  <div style={styles.summaryValue}>{data.batch_summary.total_batches}</div>
                </div>
                <div style={styles.summaryCard}>
                  <div style={styles.summaryLabel}>Animals Raised</div>
                  <div style={styles.summaryValue}>{formatNumber(data.batch_summary.animals_raised)}</div>
                </div>
                <div style={styles.summaryCard}>
                  <div style={styles.summaryLabel}>Survival Rate</div>
                  <div style={styles.summaryValue}>{data.batch_summary.survival_rate}%</div>
                </div>
                <div style={{
                  ...styles.summaryCard,
                  borderColor: data.batch_summary.net_profit >= 0 ? '#059669' : '#DC2626',
                }}>
                  <div style={styles.summaryLabel}>Net Profit</div>
                  <div style={{
                    ...styles.summaryValue,
                    color: data.batch_summary.net_profit >= 0 ? '#059669' : '#DC2626',
                  }}>
                    {formatCurrency(data.batch_summary.net_profit)}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Order Summary Cards */}
          <h2 style={styles.sectionTitle}>Feed Purchasing Summary</h2>
          <div style={styles.summaryGrid} data-tutorial="summary-cards">
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Total Orders</div>
              <div style={styles.summaryValue}>{data.summary.total_orders}</div>
              {data.yoy_comparison.order_change_pct !== null && (
                <div style={{ ...styles.changeIndicator, color: getChangeColor(data.yoy_comparison.order_change_pct) }}>
                  {formatPercent(data.yoy_comparison.order_change_pct)} vs {data.yoy_comparison.prev_year}
                </div>
              )}
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Total Spent</div>
              <div style={styles.summaryValue}>{formatCurrency(data.summary.total_spent)}</div>
              {data.yoy_comparison.spent_change_pct !== null && (
                <div style={{ ...styles.changeIndicator, color: getChangeColor(data.yoy_comparison.spent_change_pct) }}>
                  {formatPercent(data.yoy_comparison.spent_change_pct)} vs {data.yoy_comparison.prev_year}
                </div>
              )}
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Total Weight</div>
              <div style={styles.summaryValue}>{formatNumber(data.summary.total_weight_lbs)} lbs</div>
              {data.yoy_comparison.weight_change_pct !== null && (
                <div style={{ ...styles.changeIndicator, color: getChangeColor(data.yoy_comparison.weight_change_pct) }}>
                  {formatPercent(data.yoy_comparison.weight_change_pct)} vs {data.yoy_comparison.prev_year}
                </div>
              )}
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Avg Order Value</div>
              <div style={styles.summaryValue}>{formatCurrency(data.summary.avg_order_value)}</div>
            </div>
          </div>

          {/* Monthly Chart */}
          <div style={styles.chartCard}>
            <h2 style={styles.chartTitle}>Monthly Spending</h2>
            <div style={styles.barChart}>
              {data.monthly_breakdown.map((month, index) => (
                <div key={month.month} style={styles.barGroup}>
                  <div style={styles.barContainer}>
                    <div
                      style={{
                        ...styles.bar,
                        height: `${(month.total_amount / maxMonthlyAmount) * 100}%`,
                        backgroundColor: '#E73C3E',
                      }}
                      title={`${month.month_name}: ${formatCurrency(month.total_amount)}`}
                    />
                  </div>
                  <div style={styles.barLabel}>
                    {month.month_name?.substring(0, 3)}
                  </div>
                  <div style={styles.barValue}>
                    {formatCurrency(month.total_amount)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={styles.twoColumnGrid}>
            {/* Animal Type Breakdown */}
            <div style={styles.chartCard}>
              <h2 style={styles.chartTitle}>By Animal Type</h2>
              <div style={styles.pieChartContainer}>
                {data.animal_breakdown.map((animal, index) => {
                  const percentage = (animal.total_quantity_lbs / totalAnimalQuantity) * 100;
                  const color = ANIMAL_COLORS[animal.animal_type] || ANIMAL_COLORS['OTHER'];
                  return (
                    <div key={animal.animal_type} style={styles.pieItem}>
                      <div style={styles.pieItemLeft}>
                        <div style={{ ...styles.pieColorDot, backgroundColor: color }} />
                        <span style={styles.pieItemName}>{animal.animal_type}</span>
                      </div>
                      <div style={styles.pieItemRight}>
                        <div style={styles.pieBarContainer}>
                          <div
                            style={{
                              ...styles.pieBar,
                              width: `${percentage}%`,
                              backgroundColor: color,
                            }}
                          />
                        </div>
                        <span style={styles.pieItemValue}>
                          {formatNumber(animal.total_quantity_lbs)} lbs ({percentage.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top Products */}
            <div style={styles.chartCard}>
              <h2 style={styles.chartTitle}>Top Products</h2>
              <div style={styles.productList}>
                {data.product_breakdown.slice(0, 8).map((product, index) => {
                  const percentage = (product.total_quantity_lbs / maxProductQuantity) * 100;
                  return (
                    <div key={product.formula_id} style={styles.productItem}>
                      <div style={styles.productRank}>{index + 1}</div>
                      <div style={styles.productInfo}>
                        <div style={styles.productName}>{product.formula_name}</div>
                        <div style={styles.productMeta}>
                          {formatNumber(product.total_quantity_lbs)} lbs &bull; {product.order_count} orders
                        </div>
                        <div style={styles.productBarContainer}>
                          <div
                            style={{
                              ...styles.productBar,
                              width: `${percentage}%`,
                            }}
                          />
                        </div>
                      </div>
                      <div style={styles.productAmount}>
                        {formatCurrency(product.total_amount)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Year-over-Year Comparison */}
          {data.yoy_comparison.prev_total_orders > 0 && (
            <div style={styles.comparisonCard}>
              <h2 style={styles.chartTitle}>Year-over-Year Comparison</h2>
              <table style={styles.comparisonTable}>
                <thead>
                  <tr>
                    <th style={styles.compTh}>Metric</th>
                    <th style={styles.compTh}>{data.yoy_comparison.prev_year}</th>
                    <th style={styles.compTh}>{data.year}</th>
                    <th style={styles.compTh}>Change</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={styles.compTd}>Total Orders</td>
                    <td style={styles.compTd}>{data.yoy_comparison.prev_total_orders}</td>
                    <td style={styles.compTd}>{data.summary.total_orders}</td>
                    <td style={{ ...styles.compTd, color: getChangeColor(data.yoy_comparison.order_change_pct) }}>
                      {formatPercent(data.yoy_comparison.order_change_pct)}
                    </td>
                  </tr>
                  <tr>
                    <td style={styles.compTd}>Total Spent</td>
                    <td style={styles.compTd}>{formatCurrency(data.yoy_comparison.prev_total_spent)}</td>
                    <td style={styles.compTd}>{formatCurrency(data.summary.total_spent)}</td>
                    <td style={{ ...styles.compTd, color: getChangeColor(data.yoy_comparison.spent_change_pct) }}>
                      {formatPercent(data.yoy_comparison.spent_change_pct)}
                    </td>
                  </tr>
                  <tr>
                    <td style={styles.compTd}>Total Weight</td>
                    <td style={styles.compTd}>{formatNumber(data.yoy_comparison.prev_total_weight)} lbs</td>
                    <td style={styles.compTd}>{formatNumber(data.summary.total_weight_lbs)} lbs</td>
                    <td style={{ ...styles.compTd, color: getChangeColor(data.yoy_comparison.weight_change_pct) }}>
                      {formatPercent(data.yoy_comparison.weight_change_pct)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <PortalFooter />
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2rem 1.5rem',
    backgroundColor: '#FAF9F6',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '2rem',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  headerLeft: {},
  title: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#3F2021',
    margin: '0 0 0.5rem 0',
  },
  subtitle: {
    fontSize: '1rem',
    color: '#508590',
    margin: 0,
  },
  headerActions: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
  },
  yearSelect: {
    padding: '0.75rem 1rem',
    borderRadius: '4px',
    border: '2px solid #C3AD8B',
    fontSize: '1rem',
    backgroundColor: '#FFFFFF',
    fontWeight: '600',
  },
  downloadButton: {
    backgroundColor: '#6C8062',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '0.95rem',
  },
  loadingState: {
    textAlign: 'center',
    padding: '3rem',
    color: '#508590',
    fontSize: '1.125rem',
  },
  errorState: {
    backgroundColor: '#fee2e2',
    border: '1px solid #fca5a5',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '4px',
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#3F2021',
    margin: '0 0 1rem 0',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '1rem',
    marginBottom: '2rem',
  },
  summaryCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    padding: '1.5rem',
    textAlign: 'center',
  },
  summaryLabel: {
    fontSize: '0.875rem',
    color: '#508590',
    textTransform: 'uppercase',
    marginBottom: '0.5rem',
    fontWeight: '600',
  },
  summaryValue: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#3F2021',
  },
  changeIndicator: {
    fontSize: '0.875rem',
    marginTop: '0.5rem',
    fontWeight: '500',
  },
  chartCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    padding: '1.5rem',
    marginBottom: '1.5rem',
  },
  chartTitle: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#3F2021',
    margin: '0 0 1.5rem 0',
  },
  barChart: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    height: '200px',
    gap: '0.5rem',
    paddingTop: '1rem',
  },
  barGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
    height: '100%',
  },
  barContainer: {
    flex: 1,
    width: '100%',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  bar: {
    width: '80%',
    maxWidth: '40px',
    borderRadius: '4px 4px 0 0',
    transition: 'height 0.3s ease',
    minHeight: '4px',
  },
  barLabel: {
    fontSize: '0.75rem',
    color: '#508590',
    marginTop: '0.5rem',
    fontWeight: '500',
  },
  barValue: {
    fontSize: '0.65rem',
    color: '#3F2021',
    marginTop: '0.25rem',
  },
  twoColumnGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '1.5rem',
  },
  pieChartContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  pieItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
  },
  pieItemLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    minWidth: '100px',
  },
  pieColorDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
  },
  pieItemName: {
    fontSize: '0.9rem',
    color: '#3F2021',
    fontWeight: '500',
  },
  pieItemRight: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  pieBarContainer: {
    flex: 1,
    height: '8px',
    backgroundColor: '#f3f4f6',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  pieBar: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },
  pieItemValue: {
    fontSize: '0.8rem',
    color: '#508590',
    minWidth: '140px',
    textAlign: 'right',
  },
  productList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  productItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '0.75rem',
    backgroundColor: '#FAF9F6',
    borderRadius: '4px',
  },
  productRank: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#E73C3E',
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.8rem',
    fontWeight: '600',
  },
  productInfo: {
    flex: 1,
  },
  productName: {
    fontSize: '0.9rem',
    fontWeight: '500',
    color: '#3F2021',
    marginBottom: '0.25rem',
  },
  productMeta: {
    fontSize: '0.75rem',
    color: '#508590',
    marginBottom: '0.5rem',
  },
  productBarContainer: {
    height: '4px',
    backgroundColor: '#e5e7eb',
    borderRadius: '2px',
    overflow: 'hidden',
  },
  productBar: {
    height: '100%',
    backgroundColor: '#6C8062',
    borderRadius: '2px',
  },
  productAmount: {
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#3F2021',
    minWidth: '80px',
    textAlign: 'right',
  },
  comparisonCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    padding: '1.5rem',
    marginTop: '1.5rem',
  },
  comparisonTable: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  compTh: {
    textAlign: 'left',
    padding: '0.75rem',
    borderBottom: '2px solid #C3AD8B',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#3F2021',
    backgroundColor: '#FAF9F6',
  },
  compTd: {
    padding: '0.75rem',
    borderBottom: '1px solid #C3AD8B',
    fontSize: '0.95rem',
    color: '#3F2021',
  },
};

export default PortalAnnualReport;
